import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { IMAGES } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 lg:pt-44">
      <div
        aria-hidden
        className="absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-blush-50 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 lg:grid-cols-12 lg:px-10 lg:pb-36">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            data-testid="hero-eyebrow"
            className="mb-8 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.4em] text-blush-500"
          >
            <span className="h-px w-10 bg-blush-500" />
            Centro Estetico — Carpi
          </motion.p>

          <h1 data-testid="hero-title" className="font-serif text-[13vw] leading-[0.95] text-ink sm:text-7xl lg:text-[6.5rem]">
            <MaskedLine delay={0.35}>La tua bellezza,</MaskedLine>
            <MaskedLine delay={0.5} className="italic text-blush-500">
              esaltata.
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="mt-10 max-w-md text-base leading-relaxed text-ink-soft"
          >
            Extension ciglia, laminazione e rituali di benessere su misura.
            Un rifugio rosa nel cuore di Carpi, dove ogni dettaglio è curato
            con precisione e amore.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1 }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <a
              href="#prenota"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#prenota", { duration: 1.8 }); }}
              data-testid="hero-cta-prenota"
              className="group inline-flex items-center gap-3 rounded-full bg-ink px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-blush-50 transition-colors duration-500 hover:bg-blush-600"
            >
              Prenota ora
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </a>
            <a
              href="#servizi"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#servizi", { offset: -72, duration: 1.6 }); }}
              data-testid="hero-cta-servizi"
              className="text-xs font-semibold uppercase tracking-[0.25em] text-ink underline decoration-blush-500 decoration-2 underline-offset-8 transition-colors duration-300 hover:text-blush-600"
            >
              Scopri i servizi
            </a>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.55 }}
            className="relative"
          >
            <div aria-hidden className="absolute -inset-4 rounded-t-full bg-blush-100" />
            <div className="relative overflow-hidden rounded-t-full" style={{ aspectRatio: "3/4" }}>
              <motion.img
                src={IMAGES.hero}
                alt="Primo piano occhio con extension ciglia"
                data-testid="hero-image"
                style={{ y: imgY, scale: 1.15 }}
                className="h-full w-full object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.2 }}
              data-testid="hero-badge"
              className="absolute -bottom-5 -left-5 flex items-center gap-3 rounded-full border border-blush-100 bg-cream/90 py-3 pl-4 pr-6 shadow-xl shadow-blush-100/60 backdrop-blur-md"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blush-50 text-blush-600">
                <MapPin size={16} />
              </span>
              <div>
                <p className="font-serif text-lg italic leading-none text-ink">Carpi, Italia</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-ink-soft">Estetica di lusso</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
